// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes — Shared Leads Pain theme
export const SCENE_COLORS = {
  scene1: { primaryColor: '#EF4444', accentColor: '#ffffff' },   // Red/White - Paying
  scene2: { primaryColor: '#F59E0B', accentColor: '#ffffff' },   // Amber/White - Shared
  scene3: { primaryColor: '#ffffff', accentColor: '#EF4444' },   // White/Red - Platforms
  scene4: { primaryColor: '#DC2626', accentColor: '#FCD34D' },   // Deep Red/Gold - Bidding
  scene5: { primaryColor: '#22C55E', accentColor: '#ffffff' },   // Green/White - Exclusive
  scene6: { primaryColor: '#FCD34D', accentColor: '#ffffff' },   // Gold/White - CTA
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },
};

export const SCENE_IMAGES = {
  scene1: 'images/pvm-stop-feeding-shared-leads/scene1-paying.png',
  scene2: 'images/pvm-stop-feeding-shared-leads/scene2-shared.png',
  scene3: 'images/pvm-stop-feeding-shared-leads/scene3-platforms.png',
  scene4: 'images/pvm-stop-feeding-shared-leads/scene4-bidding.png',
  scene5: 'images/pvm-stop-feeding-shared-leads/scene5-exclusive.png',
  scene6: 'images/pvm-stop-feeding-shared-leads/scene6-report.png',
};

export const REPORT_SLIDES = [
  'images/pvm-quiet-phone/report-slide-01.png',
  'images/pvm-quiet-phone/report-slide-05.png',
  'images/pvm-quiet-phone/report-slide-07.png',
  'images/pvm-quiet-phone/report-slide-12.png',
];

export const DRAMATIC_WORDS = ['five', 'Shared', 'lowest', 'alone?', 'Free'];

// Word timings from Whisper — manual corrections: "leed"→"lead" for display, "leeds"→"leads" for display, "calling,"→"calling.", capitalize "The"/"Market"/"Report", "10"→"Ten"
export const WORD_TIMINGS: WordTiming[] = [
  // Scene 1: Paying for leads
  { text: "You're", startMs: 0, endMs: 140 },
  { text: 'paying', startMs: 140, endMs: 380 },
  { text: 'for', startMs: 380, endMs: 580 },
  { text: 'leads.', startMs: 580, endMs: 1100 },
  { text: 'But', startMs: 1100, endMs: 2640 },
  { text: 'so', startMs: 2640, endMs: 2780 },
  { text: 'are', startMs: 2780, endMs: 2900 },
  { text: 'five', startMs: 2900, endMs: 3040 },
  { text: 'other', startMs: 3040, endMs: 3320 },
  { text: 'contractors.', startMs: 3320, endMs: 4120 },

  // Scene 2: Shared lead platforms
  { text: 'Shared', startMs: 4120, endMs: 4960 },
  { text: 'lead', startMs: 4960, endMs: 5140 },
  { text: 'platforms.', startMs: 5140, endMs: 5880 },
  { text: 'They', startMs: 5880, endMs: 6520 },
  { text: 'sell', startMs: 6520, endMs: 6720 },
  { text: 'the', startMs: 6720, endMs: 6840 },
  { text: 'same', startMs: 6840, endMs: 7080 },
  { text: 'lead', startMs: 7080, endMs: 7340 },
  { text: 'to', startMs: 7340, endMs: 7480 },
  { text: 'everyone.', startMs: 7480, endMs: 8320 },
  { text: 'Same', startMs: 8320, endMs: 9600 },
  { text: 'customer.', startMs: 9600, endMs: 10380 },
  { text: 'Five', startMs: 10380, endMs: 11180 },
  { text: 'guys', startMs: 11180, endMs: 11400 },
  { text: 'calling.', startMs: 11400, endMs: 11960 },

  // Scene 3: Lowest bid
  { text: 'The', startMs: 11960, endMs: 12160 },
  { text: 'lowest', startMs: 12160, endMs: 12380 },
  { text: 'bid', startMs: 12380, endMs: 12600 },
  { text: 'wins.', startMs: 12600, endMs: 13040 },
  { text: 'Not', startMs: 13040, endMs: 13760 },
  { text: 'the', startMs: 13760, endMs: 13880 },
  { text: 'best', startMs: 13880, endMs: 14020 },
  { text: 'contractor.', startMs: 14020, endMs: 14780 },

  // Scene 4: What if
  { text: 'What', startMs: 14780, endMs: 15420 },
  { text: 'if', startMs: 15420, endMs: 15580 },
  { text: 'every', startMs: 15580, endMs: 15840 },
  { text: 'lead', startMs: 15840, endMs: 16120 },
  { text: 'was', startMs: 16120, endMs: 16300 },
  { text: 'yours', startMs: 16300, endMs: 16480 },
  { text: 'alone?', startMs: 16480, endMs: 17080 },

  // Scene 5: Exclusive
  { text: 'No', startMs: 17080, endMs: 17680 },
  { text: 'sharing.', startMs: 17680, endMs: 18200 },
  { text: 'No', startMs: 18200, endMs: 18460 },
  { text: 'bidding', startMs: 18460, endMs: 18720 },
  { text: 'wars.', startMs: 18720, endMs: 19240 },
  { text: 'Just', startMs: 19240, endMs: 19840 },
  { text: 'your', startMs: 19840, endMs: 20000 },
  { text: 'phone', startMs: 20000, endMs: 20180 },
  { text: 'ringing.', startMs: 20180, endMs: 20580 },

  // Scene 6: CTA
  { text: 'Free', startMs: 20580, endMs: 20820 },
  { text: 'Market', startMs: 20820, endMs: 21060 },
  { text: 'Report.', startMs: 21060, endMs: 21680 },
  { text: 'Ten', startMs: 21680, endMs: 21900 },
  { text: 'minutes.', startMs: 21900, endMs: 22360 },
  { text: 'Link', startMs: 22360, endMs: 22700 },
  { text: 'below.', startMs: 22700, endMs: 23120 },
];

export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Paying',
    startMs: 0,
    endMs: 4120,
    words: WORD_TIMINGS.slice(0, 10),
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Shared',
    startMs: 4120,
    endMs: 11960,
    words: WORD_TIMINGS.slice(10, 25),
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Bidding',
    startMs: 11960,
    endMs: 14780,
    words: WORD_TIMINGS.slice(25, 33),
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 4,
    name: 'What If',
    startMs: 14780,
    endMs: 17080,
    words: WORD_TIMINGS.slice(33, 40),
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 5,
    name: 'Exclusive',
    startMs: 17080,
    endMs: 20580,
    words: WORD_TIMINGS.slice(40, 49),
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 6,
    name: 'CTA',
    startMs: 20580,
    endMs: 23120,
    words: WORD_TIMINGS.slice(49),
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

export const LOGO_SCENE = {
  startMs: 23120,
  endMs: 28220,
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
